import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-thresholds',
  templateUrl: './thresholds.component.html',
  standalone: false,
})
export class ThresholdsComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;

  private destroy$ = new Subject<void>();

  get hasErrors(): boolean {
    const fields = [
      'alertThreshold',
      'toleranceThreshold',
      'minAlertThreshold',
      'minToleranceThreshold',
    ];
    return fields.some((f) => {
      const ctrl = this.formGroup.get(f);
      return ctrl && ctrl.invalid && ctrl.touched;
    });
  }

  ngOnInit(): void {
    this.setupValidators();
    this.setupCrossValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupValidators(): void {
    this.formGroup
      .get('alertThreshold')
      ?.setValidators(this.alertThresholdValidator());
    this.formGroup
      .get('toleranceThreshold')
      ?.setValidators(this.toleranceThresholdValidator());
    this.formGroup
      .get('minAlertThreshold')
      ?.setValidators(this.minAlertThresholdValidator());
    this.formGroup
      .get('minToleranceThreshold')
      ?.setValidators(this.minToleranceThresholdValidator());

    // Trigger initial validation
    [
      'alertThreshold',
      'toleranceThreshold',
      'minAlertThreshold',
      'minToleranceThreshold',
    ].forEach((f) =>
      this.formGroup.get(f)?.updateValueAndValidity({ emitEvent: false }),
    );
  }

  private setupCrossValidation(): void {
    const thresholdFields = [
      'alertThreshold',
      'toleranceThreshold',
      'minAlertThreshold',
      'minToleranceThreshold',
    ];

    thresholdFields.forEach((fieldName) => {
      this.formGroup
        .get(fieldName)!
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          thresholdFields.forEach((otherField) => {
            if (otherField !== fieldName) {
              this.formGroup
                .get(otherField)
                ?.updateValueAndValidity({ emitEvent: false });
            }
          });
        });
    });
  }

  private alertThresholdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const alertThreshold = control.value;
      const minAlertThreshold = this.formGroup?.get('minAlertThreshold')?.value;

      if (alertThreshold == null && minAlertThreshold == null) {
        return { atLeastOneAlertThresholdRequired: true };
      }
      if (alertThreshold == null) return null;

      const toleranceThreshold =
        this.formGroup?.get('toleranceThreshold')?.value;
      const minToleranceThreshold = this.formGroup?.get(
        'minToleranceThreshold',
      )?.value;

      if (
        (minAlertThreshold != null && alertThreshold < minAlertThreshold) ||
        (minToleranceThreshold != null &&
          alertThreshold < minToleranceThreshold) ||
        (toleranceThreshold != null && alertThreshold < toleranceThreshold)
      ) {
        return { alertThresholdTooLow: { alertThreshold, minAlertThreshold } };
      }
      return null;
    };
  }

  private toleranceThresholdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const toleranceThreshold = control.value;
      if (toleranceThreshold == null) return null;

      const alertThreshold = this.formGroup?.get('alertThreshold')?.value;
      const minToleranceThreshold = this.formGroup?.get(
        'minToleranceThreshold',
      )?.value;
      const minAlertThreshold = this.formGroup?.get('minAlertThreshold')?.value;

      if (toleranceThreshold > alertThreshold) {
        return { toleranceThresholdTooHigh: true };
      }
      if (
        (minToleranceThreshold != null &&
          toleranceThreshold < minToleranceThreshold) ||
        (minAlertThreshold != null && toleranceThreshold < minAlertThreshold)
      ) {
        return { toleranceThresholdTooLow: true };
      }
      return null;
    };
  }

  private minAlertThresholdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const minAlertThreshold = control.value;
      const alertThreshold = this.formGroup?.get('alertThreshold')?.value;

      if (minAlertThreshold == null && alertThreshold == null) {
        return { atLeastOneAlertThresholdRequired: true };
      }
      if (minAlertThreshold == null) return null;

      const toleranceThreshold =
        this.formGroup?.get('toleranceThreshold')?.value;
      const minToleranceThreshold = this.formGroup?.get(
        'minToleranceThreshold',
      )?.value;

      if (
        (alertThreshold != null && minAlertThreshold > alertThreshold) ||
        (toleranceThreshold != null &&
          minAlertThreshold > toleranceThreshold) ||
        (minToleranceThreshold != null &&
          minAlertThreshold > minToleranceThreshold)
      ) {
        return { minAlertThresholdTooHigh: true };
      }
      return null;
    };
  }

  private minToleranceThresholdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const minToleranceThreshold = control.value;
      if (minToleranceThreshold == null) return null;

      const alertThreshold = this.formGroup?.get('alertThreshold')?.value;
      const minAlertThreshold = this.formGroup?.get('minAlertThreshold')?.value;
      const toleranceThreshold =
        this.formGroup?.get('toleranceThreshold')?.value;

      if (
        minAlertThreshold != null &&
        minToleranceThreshold < minAlertThreshold
      ) {
        return { minToleranceThresholdTooLow: true };
      }
      if (
        (alertThreshold != null && minToleranceThreshold > alertThreshold) ||
        (toleranceThreshold != null &&
          minToleranceThreshold > toleranceThreshold)
      ) {
        return { minToleranceThresholdTooHigh: true };
      }
      return null;
    };
  }

  getFieldError(fieldName: string): string | null {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.invalid || !control.touched) return null;
    const errors = control.errors;
    if (!errors) return null;
    if (errors['atLeastOneAlertThresholdRequired'])
      return "Au moins un seuil d'alerte (max ou min) est requis";
    if (errors['alertThresholdTooLow'])
      return 'Doit être supérieur ou égal aux seuils de tolérance et minimaux';
    if (errors['toleranceThresholdTooHigh'])
      return "Ne peut pas être supérieur au seuil d'alerte";
    if (errors['toleranceThresholdTooLow'])
      return 'Doit être supérieur ou égal aux seuils minimaux';
    if (errors['minAlertThresholdTooHigh'])
      return "Ne peut pas être supérieur au seuil d'alerte ou de tolérance";
    if (errors['minToleranceThresholdTooLow'])
      return "Doit être supérieur ou égal au seuil d'alerte minimal";
    if (errors['minToleranceThresholdTooHigh'])
      return "Ne peut pas être supérieur au seuil d'alerte ou de tolérance";
    return 'Valeur invalide';
  }
}
